import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { getConfig } from '@/../config/environment';

declare global {
  interface Window {
    TalkdeskChatSDK?: any;
  }
}

interface TalkdeskProps {
  touchpointId: string;
  accountId: string;
  region: string;
}

interface TalkdeskConfigs {
  enableValidation: boolean;
  enableEmoji: boolean;
  enableUserInput: boolean;
  enableAttachments: boolean;
  enableResponsiveLayout: boolean;
  enablePointMoveTriggerButton: boolean;
  enableChatHeader: boolean;
  languageCode: string;
  styles: {
    triggerButtonPositionRight: string;
    triggerButtonPositionBottom: string;
    chatThemeColor?: string;
    chatHoverThemeColor?: string;
    chatLauncherColor?: string;
    chatContainerHeight?: string;
    chatPositionBottom?: string;
  };
}

// The SDK instance returned by TalkdeskChatSDK() — not window.tdWebchat,
// which the browser resolves to the container div element (same id).
let webchat: any = null;
let initialized = false;
let isLoading = false;
let openWhenReady = false;

let onOpenCb: (() => void) | null = null;
let onCloseCb: (() => void) | null = null;
let contextParams: Record<string, string> | null = null;
// Tracked so a context that resolves late can still be pushed — see pushContextParams.
let isChatOpen = false;
let activeTouchpointId: string | null = null;
let keyboardListeners: Promise<PluginListenerHandle>[] = [];

/**
 * Publishes the on-screen keyboard height so the chat iframe can shrink out of its way.
 *
 * The composer belongs to Talkdesk and lives in a cross-origin iframe, so it can't be scrolled
 * into view from here — the iframe's own box is the only lever. `capacitor.config.ts` sets
 * `Keyboard.resize = None` and `ios.contentInset = 'never'`, so in the native shell the keyboard
 * merely overlays the WKWebView: neither `window.innerHeight` nor `visualViewport` shrink, and
 * `100dvh` stays at full screen height. That is why the input is occluded in the IPA but not in a
 * browser, where the OS reflows the page itself.
 */
function setKeyboardOffset(height: number) {
  const root = document.documentElement;
  root.style.setProperty('--td-keyboard-height', `${height}px`);
  // The `bottom` half of the compensation is scoped to "keyboard up" because the SDK owns the
  // iframe's positioning: forcing a `bottom` at rest could move a chat that renders fine today.
  if (height > 0) root.dataset.tdKeyboard = 'open';
  else delete root.dataset.tdKeyboard;
}

/**
 * iOS only. In the browser the OS already reflows the page, so there is nothing to correct.
 * On Android, `capacitor.config.ts`'s `Keyboard.resize = None` is a no-op — Capacitor's Android
 * plugin never implements resize-mode switching — so the Activity keeps the manifest default,
 * `windowSoftInputMode` unset, which resolves to `adjustResize`. That means Android's WebView
 * viewport (and therefore `100dvh`) already shrinks by the keyboard height on its own. Applying
 * this same `--td-keyboard-height` compensation there would subtract the keyboard height a
 * second time, leaving the iframe far shorter than the screen below the keyboard — confirmed by
 * measuring `window.innerHeight` drop from 866px to 578px on a real on-screen keyboard, then the
 * iframe height computing to a further-reduced 298px instead of the correct 578px.
 */
function attachKeyboardListeners() {
  if (Capacitor.getPlatform() !== 'ios' || keyboardListeners.length > 0) return;
  // `will*` rather than `did*`: the iframe then resizes along with the keyboard animation
  // instead of snapping into place after it.
  keyboardListeners = [
    Keyboard.addListener('keyboardWillShow', info => setKeyboardOffset(info.keyboardHeight)),
    Keyboard.addListener('keyboardWillHide', () => setKeyboardOffset(0)),
  ];
}

function detachKeyboardListeners() {
  keyboardListeners.forEach(listener => listener.then(l => l.remove()));
  keyboardListeners = [];
  setKeyboardOffset(0);
}

export function setTalkdeskTouchpointId(id: string) {
  if (activeTouchpointId === id) return;
  if (activeTouchpointId !== null) {
    // ID changed — destroy the existing instance so the next call to
    // initializeTalkdeskChat re-initialises with the correct touchpoint.
    destroyTalkdeskChat();
  }
  activeTouchpointId = id;
}

/**
 * Diagnostic gate for the context payload sent to Talkdesk.
 *
 * On in dev automatically; in a staging/native build set `localStorage['td-debug'] = '1'` and
 * reopen the chat — rebuilding an IPA/APK just to read one payload is not practical. Reading
 * localStorage can throw (private mode, blocked site data), so it is guarded.
 */
function tdDebugEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem('td-debug') === '1';
  } catch {
    return false;
  }
}

/**
 * Hands the staged identity to the SDK. `trigger` only labels the diagnostic line.
 */
function pushContextParams(trigger: 'open' | 'context-changed') {
  if (!webchat || !contextParams) return;

  if (tdDebugEnabled()) {
    // The payload, verbatim, at the moment it crosses into the SDK. Anything the agent sees that
    // is not in here did not come from this app.
    console.warn(`[talkdesk] setContextParam (${trigger}):`, JSON.stringify(contextParams, null, 2));
    console.table(
      Object.entries(contextParams).map(([field, value]) => ({
        field,
        value,
        empty: value === '' || value == null,
      }))
    );
  }

  webchat.setContextParam(contextParams);
}

export function setTalkdeskContext(params: Record<string, string>) {
  contextParams = params;
  if (tdDebugEnabled()) console.warn('[talkdesk] context staged:', params);

  // Staging alone only reaches Talkdesk via onOpenWebchat, so a context that resolves *after* the
  // chat is already open would never be sent. That is a real ordering risk rather than a
  // theoretical one: appProvider awaits getUserProfile() after setUser, so `profile.phone` — the
  // source for field_number1 — lands late, and opening the concierge during that window otherwise
  // sends an empty phone for the whole conversation. When the chat is closed there is nothing to
  // do: onOpenWebchat pushes whatever is staged at open time.
  if (isChatOpen) pushContextParams('context-changed');
}

export function setTalkdeskCallbacks(callbacks: { onOpen?: () => void; onClose?: () => void }) {
  onOpenCb = callbacks.onOpen ?? null;
  onCloseCb = callbacks.onClose ?? null;
}

export function initializeTalkdeskChat() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Fully ready — open immediately.
  if (initialized) {
    webchat.open();
    return;
  }

  // Still loading or initialising — just mark to open when ready.
  if (isLoading) {
    openWhenReady = true;
    return;
  }

  isLoading = true;
  openWhenReady = true;

  const divContainer = document.createElement('div');
  divContainer.id = 'tdWebchat';
  document.body.appendChild(divContainer);

  const script = document.createElement('script');
  const firstScript = document.getElementsByTagName('script')[0];

  script.type = 'text/javascript';
  script.id = 'tdwebchatscript';
  script.src = 'https://talkdeskchatsdk.talkdeskapp.com/v2/talkdeskchatsdk.js';
  script.async = true;

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  const props: TalkdeskProps = {
    touchpointId: activeTouchpointId ?? getConfig().talkdeskTouchpointId ?? '',
    accountId: '',
    region: 'td-us-1',
  };

  const configs: TalkdeskConfigs = {
    enableEmoji: true,
    enableValidation: true,
    enableUserInput: true,
    enableAttachments: true,
    enableResponsiveLayout: true,
    enablePointMoveTriggerButton: false,
    enableChatHeader: true,
    languageCode: '',
    styles: {
      triggerButtonPositionRight: '-9999px',
      triggerButtonPositionBottom: '-9999px',
      chatThemeColor: '#1A1A1A',
      chatHoverThemeColor: '#007AFF',
      chatLauncherColor: '#1A1A1A',
      chatContainerHeight: undefined,
      chatPositionBottom: undefined,
    },
  };

  script.onload = () => {
    if (!window.TalkdeskChatSDK) return;

    // TalkdeskChatSDK() returns the SDK instance — store it at module level.
    webchat = window.TalkdeskChatSDK('tdWebchat', props);
    webchat.onOpenWebchat = () => {
      isChatOpen = true;
      if (contextParams) {
        pushContextParams('open');
      } else if (tdDebugEnabled()) {
        // Distinguishes "nothing was sent" from "debug was not running" — the chat can open
        // before the user/profile resolves, and then no context reaches Talkdesk at all.
        // setTalkdeskContext pushes on its own once the identity does resolve.
        console.warn('[talkdesk] chat opened with NO context params — nothing sent yet.');
      }
      onOpenCb?.();
    };
    webchat.onCloseWebchat = () => {
      isChatOpen = false;
      onCloseCb?.();
    };
    webchat.init(configs).then(() => {
      initialized = true;
      isLoading = false;

      // Push the chat iframe below the device status bar using the safe-area inset, and keep its
      // bottom edge clear of the on-screen keyboard (see setKeyboardOffset). With the keyboard
      // down `--td-keyboard-height` is unset, so the height resolves to plain `100dvh` as before.
      // Both `height` and `bottom` are compensated because the SDK owns the iframe's positioning:
      // shrinking the height covers a top-anchored box, the `bottom` lift covers a bottom-anchored
      // one, and whichever does not apply is inert.

      const style = document.createElement('style');
      style.id = 'td-safe-area';
      style.textContent = `
        #tdWebchat iframe {
          background-color:white!important;
          padding-top: env(safe-area-inset-top, 0px)!important;
          height: calc(100dvh - var(--td-keyboard-height, 0px)) !important;
        }
        html[data-td-keyboard='open'] #tdWebchat iframe {
          bottom: var(--td-keyboard-height, 0px) !important;
        }
      `;
      document.head.appendChild(style);

      attachKeyboardListeners();

      if (openWhenReady) {
        openWhenReady = false;
        // Defer open() — the SDK's promise resolves before its UI is fully rendered.
        setTimeout(() => webchat.open(), 300);
      }
    });
  };

  script.onerror = () => {
    console.error('Failed to load TalkDesk chat script');
    isLoading = false;
  };
}

export function openTalkdeskChat() {
  webchat?.open();
}

export function closeTalkdeskChat() {
  webchat?.close();
}

export function destroyTalkdeskChat() {
  webchat?.destroy();
  document.getElementById('td-safe-area')?.remove();
  detachKeyboardListeners();
  webchat = null;
  initialized = false;
  isLoading = false;
  // destroy() tears the widget down without necessarily firing onCloseWebchat, so reset the flag
  // here too — otherwise a stale `true` would make setTalkdeskContext push into a null instance.
  isChatOpen = false;
}
